import * as React from 'react';

import { Input, Select, Textarea, Title } from 'common/components';
import { STATUSES_OPTIONS } from 'common/consts';
import { CoreProps, PK } from 'common/types';
import {
  CoreFormLayout,
  FieldsGrid,
  GridItem,
  Section,
  SectionsWrapper,
} from 'templates/core';
import { TitleInput } from 'templates/details';

import { useProgramCreate, useProgramUpdate } from '../queries';
import { ProgramFields, validationSchema, fields } from '../consts';
import ProgramAssembler from './ProgramAssembler';
import ProgramMediaPicker from './ProgramMediaPicker';
import ProgramTagsPicker from './ProgramTagsPicker';
import { useToast } from 'modules/toast';
import { toastMessages } from 'common/toastMessages';
import ProgramBlogPicker from 'modules/blogs/components/ProgramBlogPicker';
import ProgramCategorySelect from './ProgramCategorySelect';

const useSave = (isTags: boolean, pk?: PK, blog_pk?: PK) => {
  const [created, setCreated] = React.useState(pk);
  const { toast } = useToast();
  const create = useProgramCreate();
  const update = useProgramUpdate();
  const isSaving = create.isLoading || update.isLoading;
  const error = create.error || update.error;

  const save = (values: ProgramFields) => {
    if (values?.status[0].value === 'ACTIVE' && !isTags) {
      toast(toastMessages.ERROR.CHANGING_STATUS_WITHOUT_TAGS, 'error');
      return;
    }

    const payload = {
      title: values[fields.title.name],
      description: values[fields.description.name],
      short_description: values[fields.short_description.name],
      category: values[fields.category.name][0].value,
      blog_pk,
    };
    if (created) {
      update.mutate({ ...payload, pk: created, status: values.status[0].value });
    } else {
      create.mutate(payload, {
        onSuccess: ({ pk }) => {
          setCreated(pk);
        },
      });
    }
  };

  return { save, created, isSaving, error };
};

const ProgramCore = ({
  pk,
  blog_pk,
  defaultValues,
  layout = 'overview',
}: CoreProps<ProgramFields>) => {
  const [isTags, setIsTags] = React.useState(false);
  const { save, isSaving, error, created } = useSave(isTags, pk, blog_pk);

  return (
    <>
      <CoreFormLayout
        onSubmit={save}
        defaultValues={defaultValues}
        validationSchema={validationSchema}
        isSubmitting={isSaving}
        error={error}
      >
        {layout === 'overview' && (
          <Section>
            <GridItem>
              <TitleInput name={fields.title.name} />
            </GridItem>
          </Section>
        )}
        <Section>
          <FieldsGrid>
            <GridItem compact>
              <Select
                name={fields.status.name}
                options={STATUSES_OPTIONS}
                readOnly={!created}
              />
            </GridItem>
            {layout === 'creation' && (
              <GridItem>
                <Input name={fields.title.name} label={fields.title.label} />
              </GridItem>
            )}
          </FieldsGrid>
        </Section>
        <Section>
          <Title marginBottom={2.5}>General</Title>
          <FieldsGrid>
            <GridItem>
              <Textarea
                name={fields.short_description.name}
                label={fields.short_description.label}
              />
            </GridItem>
            <GridItem>
              <Textarea name={fields.description.name} label={fields.description.label} />
            </GridItem>
            <GridItem compact>
              <ProgramCategorySelect
                isSingleChoiceResettable
                name={fields.category.name}
                label={fields.category.label}
              />
            </GridItem>
          </FieldsGrid>
        </Section>
      </CoreFormLayout>
      <SectionsWrapper pk={created}>
        <ProgramTagsPicker setIsTags={setIsTags} />
        <ProgramMediaPicker />
        <ProgramAssembler />
        <ProgramBlogPicker />
      </SectionsWrapper>
    </>
  );
};

export default ProgramCore;
